import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import { db } from '@/services/storage/db';
import { LogEntryService } from '@/services/log-entry-service';
import { SeasonRecapService } from '@/services/season-recap-service';
import type { Season } from '@/types/season';
import type { LogEntry } from '@/types/log-entry';
import { CollapsibleWidget } from './collapsible-widget';
import { computeTopStandByObservations } from '@/utils/season-recap-utils';
import { localTodayISO } from '@/utils/date-utils';

// Module-level service singletons — same pattern as stores (log-entry-store.ts, pattern-store.ts).
// Components never call db.* directly; all persistence goes through service methods.
const logEntryService = new LogEntryService(db);
const seasonRecapService = new SeasonRecapService(db);

// ---------------------------------------------------------------------------
// DASH-D1/M102 — collapsed-by-default persistence reconcile (AC #2 / A1 ruling)
// ---------------------------------------------------------------------------
// CollapsibleWidget reads `ridgen-widget-season-recap` only in its useState initializer,
// and a stored 'true' OVERRIDES defaultExpanded={false} (collapsible-widget.tsx:28-34). So
// defaultExpanded={false} ALONE re-expands the card for any user who previously expanded it.
// This one-time keyed migration normalizes the legacy expanded-persisted value to the new
// collapsed default exactly once: if a stale 'true' is present (and the migration has not yet
// run), it is removed so defaultExpanded={false} wins. After the marker is set, a user who
// later deliberately expands the card keeps their persisted preference — the migration never
// re-evicts. Scoped to SeasonRecapCard; collapsible-widget.tsx is NOT touched (RULE-276).
const SEASON_RECAP_WIDGET_KEY = 'ridgen-widget-season-recap';
const SEASON_RECAP_COLLAPSE_MIGRATED_KEY = 'ridgen-widget-season-recap-collapse-migrated';

function reconcileSeasonRecapCollapseDefault(): void {
  if (localStorage.getItem(SEASON_RECAP_COLLAPSE_MIGRATED_KEY) === 'true') return;
  // Evict only the legacy expanded-persisted value so the new collapsed default applies.
  // A stored 'false' (explicitly collapsed) already agrees with the default and is left intact.
  if (localStorage.getItem(SEASON_RECAP_WIDGET_KEY) === 'true') {
    localStorage.removeItem(SEASON_RECAP_WIDGET_KEY);
  }
  localStorage.setItem(SEASON_RECAP_COLLAPSE_MIGRATED_KEY, 'true');
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SeasonRecapData {
  totalHarvests: number;
  totalSits: number;
  daysAfield: number;
  topStand: string | null;
  logEntries: LogEntry[];
}

export interface SeasonRecapCardProps {
  season: Season;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildCollapsedSummary(season: Season, data: SeasonRecapData): string {
  const topPart = data.topStand ? `Top: ${data.topStand}` : 'Top: N/A';
  return `${season.name} · ${data.totalHarvests}H · ${data.totalSits} sits · ${topPart}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SeasonRecapCard({ season }: SeasonRecapCardProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<SeasonRecapData | null>(null);
  // Run the one-time collapse-default reconcile synchronously during the first render of this
  // parent, BEFORE the child CollapsibleWidget first mounts. The card returns null until data
  // loads (see `if (!data) return null` below), so the child's localStorage read happens on a
  // later render — this lazy initializer is guaranteed to run first. A lazy useState initializer
  // is the deterministic, per-mount-safe place for this (no effect timing race against the child).
  useState(() => {
    reconcileSeasonRecapCollapseDefault();
    return null;
  });

  const load = useCallback(async (): Promise<void> => {
    const openerDate = season.openerDate;
    const closerDate = season.closerDate ?? localTodayISO();

    const [harvests, hunts, logEntries] = await Promise.all([
      seasonRecapService.getHarvestsBySeason(season.id),
      seasonRecapService.getHuntsBySeason(season.id),
      logEntryService.getLogEntriesInDateRange(openerDate, closerDate),
    ]);

    // Build sits from all hunts in this season
    const sitDateSet = new Set<string>();
    let totalSits = 0;
    for (const hunt of hunts) {
      const sits = await seasonRecapService.getHuntSitsByHunt(hunt.id);
      totalSits += sits.length;
      sits.forEach((s) => sitDateSet.add(s.date));
    }

    const daysAfield = sitDateSet.size;

    // "Top stand" is derived from scouting log entries, not hunt sits.
    // Log entries track scouting visits; sits track active hunts (M48-M50).
    // This is intentional — the season recap reflects scouting activity.
    const waypointIds = new Set<string>(logEntries.map((e) => e.waypointId));
    const waypoints = await Promise.all(
      Array.from(waypointIds).map(async (id) => {
        const wp = await seasonRecapService.getWaypoint(id);
        return wp ? { id: wp.id, name: wp.name } : { id, name: 'Unnamed' };
      }),
    );

    const topStand = computeTopStandByObservations(logEntries, waypoints);

    const totalHarvests = harvests.length;

    setData({ totalHarvests, totalSits, daysAfield, topStand, logEntries });
  }, [season.id, season.openerDate, season.closerDate]);

  useEffect(() => {
    let active = true;

    // eslint-disable-next-line react-hooks/set-state-in-effect -- async load dispatched from effect body; setState is inside callback
    load()
      .then(() => {
        if (!active) return;
      })
      .catch((err) => {
        if (active) {
          console.error('[SeasonRecapCard] load:', err);
        }
      });

    return () => {
      active = false;
    };
  }, [load]);

  if (!data) return null;

  const { totalHarvests, totalSits, daysAfield, topStand, logEntries } = data;

  // Hide entirely if there is no season activity at all
  if (totalHarvests === 0 && totalSits === 0 && logEntries.length === 0) return null;

  const openerFormatted = format(parseISO(season.openerDate), 'MMM d, yyyy');
  const closerFormatted = season.closerDate
    ? format(parseISO(season.closerDate), 'MMM d, yyyy')
    : null;

  const collapsedText = buildCollapsedSummary(season, data);

  return (
    <CollapsibleWidget
      id="season-recap"
      title="SEASON RECAP"
      summary={collapsedText}
      defaultExpanded={false}
    >
      {/* ember-900/10 tint background — same celebratory treatment as HarvestHighlightCard */}
      <button
        type="button"
        onClick={() => navigate(`/seasons/${season.id}`)}
        className="w-full text-left bg-[var(--color-surface)] border-x border-b border-[var(--color-border)] border-t-2 border-t-[var(--color-ember-500)] rounded-b-lg rounded-t-none p-4"
        data-testid="season-recap-card"
        style={{ background: 'color-mix(in srgb, var(--color-ember-900) 10%, var(--color-surface))' }}
      >
        {/* Season name + dates */}
        <p className="font-display text-display-sm text-[var(--color-text-primary)] mb-0.5">
          {season.name}
        </p>
        {closerFormatted && (
          <p className="text-body-xs text-[var(--color-text-muted)] mb-3">
            {openerFormatted} – {closerFormatted}
          </p>
        )}

        {/* Stats row */}
        <div className="flex items-center gap-4">
          <div className="flex flex-col items-center">
            <span
              className="font-display text-display-md text-[var(--color-ember-400)]"
              data-testid="season-recap-harvests"
            >
              {totalHarvests}
            </span>
            <span className="text-body-xs text-[var(--color-text-muted)] uppercase tracking-[0.08em]">
              harvests
            </span>
          </div>

          <div className="flex flex-col items-center">
            <span
              className="font-display text-display-md text-[var(--color-text-primary)]"
              data-testid="season-recap-sits"
            >
              {totalSits}
            </span>
            <span className="text-body-xs text-[var(--color-text-muted)] uppercase tracking-[0.08em]">
              sits
            </span>
          </div>

          <div className="flex flex-col items-center">
            <span
              className="font-display text-display-md text-[var(--color-text-primary)]"
              data-testid="season-recap-days"
            >
              {daysAfield}
            </span>
            <span className="text-body-xs text-[var(--color-text-muted)] uppercase tracking-[0.08em]">
              days afield
            </span>
          </div>

          {topStand && (
            <div className="flex-1 flex flex-col items-end min-w-0">
              <span className="text-body-xs text-[var(--color-text-muted)] uppercase tracking-[0.08em] mb-0.5">
                top stand
              </span>
              <span
                className="text-body-sm font-medium text-[var(--color-text-primary)] truncate max-w-full"
                data-testid="season-recap-top-stand"
              >
                {topStand}
              </span>
            </div>
          )}
        </div>
      </button>
    </CollapsibleWidget>
  );
}
